"use client";

import { useState, useRef, useEffect } from "react";
import type { VideoRotationDemoBlock } from "@mw/shared/payload-types";
import { getImageProps, getVideoUrl } from "@utilities/payload";
import { clsx } from "clsx";
import { Tabs, TabList, Tab, TabPanel } from "react-aria-components";
import { useTranslations } from "next-intl";
import { useInView } from "@ui/lib/useInView";
import { useFocusWithin } from "react-aria";
import * as m from "motion/react-m";
import { AnimatePresence } from "motion/react";

import { VideoPlayerContent } from "@ui/components/core/VideoPlayerContent";
import { ScrollGrid } from "@ui/components/layout/ScrollGrid";
import Image from "next/image";

// Re-export for backwards compatibility
export { VideoPlayerContent } from "@ui/components/core/VideoPlayerContent";
export type { VideoPlayerContentProps } from "@ui/components/core/VideoPlayerContent";

type VideoRotationDemoProps = {
  config?: Pick<NonNullable<VideoRotationDemoBlock>, "videos" | "enableOverlay" | "layout" | "autoPlay">;
};

export function VideoRotationDemo({ config }: VideoRotationDemoProps) {
  const videos = config?.videos || [];
  const enableOverlay = config?.enableOverlay ?? false;
  const layout = config?.layout ?? "thumbnails";
  const autoPlay = config?.autoPlay ?? true;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(autoPlay);
  const [showControls, setShowControls] = useState(true);
  const [hasFocus, setHasFocus] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hideControlsTimeoutRef = useRef<number | null>(null);
  const userHasStartedPlaybackRef = useRef(false);

  const inView = useInView(containerRef, { once: true, threshold: 0.1 });

  const { focusWithinProps } = useFocusWithin({
    onFocusWithin: () => {
      setHasFocus(true);
      setShowControls(true);
      // Clear any pending hide timer
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }
    },
    onBlurWithin: () => {
      setHasFocus(false);
    },
  });

  // Auto-mute video when out of viewport only in autoplay mode.
  // When autoplay is disabled, there is no mute toggle in the UI.
  useEffect(() => {
    if (!autoPlay) return;
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting && !isMuted) {
          setIsMuted(true);
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [autoPlay, isMuted]);

  const handlePlay = () => {
    userHasStartedPlaybackRef.current = true;
    setIsPlaying(true);
  };

  // Play when the selection changes; skip initial mount only when CMS auto-play is disabled
  useEffect(() => {
    if (!videoRef.current) return;
    if (!autoPlay && !userHasStartedPlaybackRef.current) return;
    videoRef.current.play().catch(() => {
      // Autoplay might be blocked, which is fine - user can click play
    });
  }, [currentIndex, autoPlay]);

  // Auto-hide controls on mobile after 3 seconds of inactivity (but keep visible when focused)
  useEffect(() => {
    const resetHideTimer = () => {
      setShowControls(true);

      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }

      hideControlsTimeoutRef.current = window.setTimeout(() => {
        // Don't hide if something is focused
        if (!hasFocus) {
          setShowControls(false);
        }
      }, 3000);
    };

    resetHideTimer();

    return () => {
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }
    };
  }, [isPlaying, hasFocus]);

  const handleInteraction = () => {
    if (!showControls) {
      setShowControls(true);

      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }

      hideControlsTimeoutRef.current = window.setTimeout(() => {
        // Don't hide if something is focused
        if (!hasFocus) {
          setShowControls(false);
        }
      }, 3000);
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const selectVideo = (key: number) => {
    if (key !== currentIndex) {
      setCurrentIndex(key);
      // The useEffect will trigger play when videoUrl changes
    }
  };

  const handleVideoEnd = () => {
    if (!autoPlay && !userHasStartedPlaybackRef.current) return;

    if (videos.length === 1) {
      // If there's only one video, just restart it
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.play().catch(() => {
          // Play might fail, but state will be accurate via events
        });
      }
    } else {
      // Auto-rotate to next video, wrap around to first video
      const nextIndex = (currentIndex + 1) % videos.length;
      setCurrentIndex(nextIndex);
      // The useEffect will trigger play when videoUrl changes
    }
  };

  if (!videos.length) {
    return null;
  }

  // Single video - no tabs needed
  if (videos.length === 1) {
    const video = videos[0];

    return (
      <div
        ref={containerRef}
        className='tw-relative tw-w-full tw-aspect-video tw-rounded-3xl tw-overflow-hidden'
        {...focusWithinProps}
      >
        <VideoPlayerContent
          video={video}
          videoRef={videoRef}
          isMuted={isMuted}
          showControls={showControls}
          inView={inView}
          enableOverlay={enableOverlay}
          onPlay={handlePlay}
          onPause={() => setIsPlaying(false)}
          onEnded={handleVideoEnd}
          onInteraction={handleInteraction}
          toggleMute={autoPlay ? toggleMute : undefined}
          autoPlay={autoPlay}
        />
      </div>
    );
  }

  // Multiple videos - use tabs
  return (
    <Tabs
      selectedKey={currentIndex}
      onSelectionChange={key => selectVideo(Number(key))}
      ref={containerRef}
      className='tw-relative tw-flex tw-flex-col-reverse'
      {...focusWithinProps}
    >
      {layout === "thumbnails" && <ThumbnailTabs isVisible={showControls} videos={videos} inView={inView} />}
      {layout === "quotes" && <QuotesTabs videos={videos} selectedKey={currentIndex} selectVideo={selectVideo} />}
      {/* TabPanels for each video */}
      {videos.map((video, index) => (
        <TabPanel
          key={index}
          id={index}
          className='tw-relative tw-aspect-video tw-rounded-3xl tw-w-full tw-overflow-hidden tw-bg-gray-200'
        >
          <VideoPlayerContent
            video={video}
            videoRef={index === currentIndex ? videoRef : undefined}
            isMuted={isMuted}
            showControls={showControls}
            inView={inView}
            enableOverlay={enableOverlay}
            onPlay={handlePlay}
            onPause={() => setIsPlaying(false)}
            onEnded={handleVideoEnd}
            onInteraction={handleInteraction}
            autoPlay={autoPlay}
            toggleMute={autoPlay ? toggleMute : undefined}
            showProgressBar={layout === "quotes"}
            onNext={() => selectVideo((currentIndex + 1) % videos.length)}
            onPrevious={() => selectVideo((currentIndex - 1 + videos.length) % videos.length)}
            hideCtaOnMobile={layout === "thumbnails"}
            hideTextOnMobile
          />
        </TabPanel>
      ))}
    </Tabs>
  );
}

function QuotesTabs({
  videos,
  selectedKey,
  selectVideo,
}: {
  videos: NonNullable<VideoRotationDemoBlock["videos"]>;
  selectedKey: number;
  selectVideo: (key: number) => void;
}) {
  const scrollGridRef = useRef<HTMLDivElement>(null);
  const selectionFromScrollRef = useRef(false);
  const isProgrammaticScrollRef = useRef(false);
  const programmaticScrollTargetRef = useRef<number | null>(null);
  const programmaticScrollResetTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (programmaticScrollResetTimeoutRef.current) {
        window.clearTimeout(programmaticScrollResetTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (selectionFromScrollRef.current) {
      selectionFromScrollRef.current = false;
      return;
    }
    const container = scrollGridRef.current;
    if (!container) return;
    const child = container?.querySelector(`[data-key="${selectedKey}"]`);
    if (!child) return;
    const containerRect = container.getBoundingClientRect();
    const paddingLeft = parseFloat(getComputedStyle(container).paddingLeft) || 0;
    const childRect = child.getBoundingClientRect();
    let current = true;
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (!current) return;
        isProgrammaticScrollRef.current = true;
        programmaticScrollTargetRef.current = selectedKey;
        if (programmaticScrollResetTimeoutRef.current) {
          window.clearTimeout(programmaticScrollResetTimeoutRef.current);
        }
        // Fallback reset in case the scroll callbacks don't report the target index.
        programmaticScrollResetTimeoutRef.current = window.setTimeout(() => {
          isProgrammaticScrollRef.current = false;
          programmaticScrollTargetRef.current = null;
          programmaticScrollResetTimeoutRef.current = null;
        }, 1000);
        container.scrollTo({
          left: container.scrollLeft + childRect.left - containerRect.left - paddingLeft,
          behavior: "smooth",
        });
      });
    });
    return () => {
      current = false;
    };
  }, [selectedKey]);

  return (
    <div className='tw-mt-10 tw-px-2 tw-var-[full-bleed=calc(var(--full-bleed-section)+0.5rem)] sm:tw-px-8 sm:tw-var-[full-bleed=calc(var(--full-bleed-section)+2rem)]'>
      <ScrollGrid
        gap='lg'
        ref={scrollGridRef}
        as='tabs'
        items={videos}
        draggable={false}
        onChangeLeftmostIndex={index => {
          if (isProgrammaticScrollRef.current) {
            if (index === programmaticScrollTargetRef.current) {
              isProgrammaticScrollRef.current = false;
              programmaticScrollTargetRef.current = null;
              if (programmaticScrollResetTimeoutRef.current) {
                window.clearTimeout(programmaticScrollResetTimeoutRef.current);
                programmaticScrollResetTimeoutRef.current = null;
              }
            }
            return;
          }
          if (index === Number(selectedKey)) return;
          selectionFromScrollRef.current = true;
          selectVideo(index);
        }}
        renderItem={(video, _videoIndex, { isSelected }) => (
          <div className='tw-cursor-pointer'>
            <div
              className={clsx("tw-type-base tw-transition-colors", isSelected ? "tw-text-black" : "tw-text-gray-400")}
            >
              <div className='tw-relative tw-text-pretty before:tw-content-[open-quote] before:tw-absolute before:tw-top-0 before:tw-right-full after:tw-content-[close-quote]'>
                {video.quote?.quote}
              </div>
            </div>
            <div className='tw-mt-10'>
              <div
                className={clsx(
                  "tw-type-base tw-font-medium tw-transition-colors",
                  isSelected ? "tw-text-black" : "tw-text-gray-400",
                )}
              >
                {video.quote?.author}
              </div>
              <div
                className={clsx(
                  "tw-mt-2.5 tw-type-xs tw-transition-colors",
                  isSelected ? "tw-text-gray-500" : "tw-text-gray-400",
                )}
              >
                {video.quote?.role}
              </div>
            </div>
          </div>
        )}
      />
    </div>
  );
}

function ThumbnailTabs({
  isVisible,
  videos,
  inView,
}: {
  isVisible: boolean;
  videos: NonNullable<VideoRotationDemoBlock["videos"]>;
  inView: boolean;
}) {
  const t = useTranslations("ClientKeys.video_rotation_demo");

  return (
    <div
      className={clsx(
        "tw-absolute tw-bottom-4 md:tw-bottom-6 tw-z-10 tw-transition-opacity tw-duration-300",
        "tw-left-0 tw-right-0 tw-pointer-events-none",
        !isVisible && "md:tw-opacity-100 tw-opacity-0",
      )}
    >
      <div className='tw-flex tw-gap-3 tw-overflow-x-auto tw-overflow-y-hidden tw-scrollbar-none tw-p-1'>
        {/* Left spacer - grows to center content when space available, larger on desktop to clear play button */}
        <div className='tw-shrink-0 tw-flex-grow tw-min-w-4 md:tw-min-w-24 tw-basis-0 tw-pointer-events-none' />

        <TabList className={clsx("tw-flex tw-gap-3 md:tw-pointer-events-auto", isVisible && "tw-pointer-events-auto")}>
          {videos.map((video, index) => {
            const thumbImageProps = getImageProps(video.image, { placeholder: true, fill: true });
            const thumbVideoUrl = video.media ? getVideoUrl(video.media) : null;
            if (!thumbVideoUrl && !thumbImageProps) return null;

            return (
              <Tab
                key={index}
                id={index}
                className={clsx(
                  "tw-relative tw-shrink-0 tw-w-16 md:tw-w-24 tw-aspect-[3/2] tw-rounded-xl tw-transition-all tw-group tw-cursor-pointer",
                  "focus-visible:tw-outline-focus",
                )}
                aria-label={t("play_video", { title: video.title })}
              >
                {({ isSelected }) => (
                  <>
                    <div className='tw-absolute tw-inset-0 tw-rounded-xl tw-overflow-hidden'>
                      {thumbImageProps ? (
                        <Image {...thumbImageProps} alt='' objectFit='cover' />
                      ) : (
                        thumbVideoUrl &&
                        inView && (
                          <video
                            src={thumbVideoUrl}
                            className='tw-w-full tw-h-full tw-object-cover'
                            muted
                            playsInline
                            preload='metadata'
                          />
                        )
                      )}
                      <div className='tw-absolute tw-inset-0 tw-bg-black/20 group-hover:tw-bg-black/0 tw-transition-colors' />
                    </div>
                    <AnimatePresence>
                      {isSelected && (
                        <m.div
                          className='tw-absolute tw-inset-0 tw-pointer-events-none tw-ring-2 tw-ring-white tw-ring-offset-2 tw-ring-offset-black/50 tw-rounded-xl group-focus-visible:tw-ring-blue-500'
                          initial={{ scale: 0.92, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          exit={{ scale: 0.95, opacity: 0 }}
                          transition={{ duration: 0.15, ease: "easeOut" }}
                        />
                      )}
                    </AnimatePresence>
                  </>
                )}
              </Tab>
            );
          })}
        </TabList>

        {/* Right spacer - grows to center content when space available */}
        <div className='tw-shrink-0 tw-flex-grow tw-min-w-4 tw-basis-0 tw-pointer-events-none' />
      </div>
    </div>
  );
}
